import React, { forwardRef, useRef, useEffect } from 'react'
import styled from 'styled-components'
import { colors } from '../../styleGuide'

interface OtpInputProps {
  length?: number
  value?: string
  onChange?: (value: string) => void
  onComplete?: (value: string) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  autoFocus?: boolean
  size?: 'small' | 'medium' | 'large'
  variant?: 'default' | 'outlined' | 'filled' | 'standard'
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info'
  isShowError?: boolean
  errorMessage?: string
  label?: string
  isShowLabel?: boolean
  isRequired?: boolean
  helperText?: string
  isShowHelperText?: boolean
  isShowErrorMessage?: boolean
  fullWidth?: boolean
  margin?: string
  padding?: string
  borderRadius?: string
  borderWidth?: string
  borderStyle?: string
  borderColor?: string
  backgroundColor?: string
  fontSize?: string
  fontWeight?: string
  fontFamily?: string
  textAlign?: string
  letterSpacing?: string
  lineHeight?: string
  boxShadow?: string
  gap?: string
  inputWidth?: string
  inputHeight?: string
}

const OtpContainer = styled.div<{ isShowError: boolean; fullWidth: boolean }>`
  position: relative;
  width: ${(props) => (props.fullWidth ? '100%' : 'auto')};
  display: flex;
  flex-direction: column;
  gap: 8px;

  .error-message {
    color: ${colors.danger};
    font-size: 12px;
    margin-top: 4px;
    display: ${(props) => (props.isShowError ? 'block' : 'none')};
  }

  .helper-text {
    color: ${colors.grayMedium};
    font-size: 12px;
    margin-top: 4px;
  }

  .label {
    font-size: 14px;
    font-weight: 500;
    color: ${colors.grayDarker};
    margin-bottom: 4px;
  }

  .required {
    color: ${colors.danger};
  }
`

const OtpInputGroup = styled.div<{ gap: string }>`
  display: flex;
  justify-content: center;
  gap: ${(props) => props.gap};
  align-items: center;
`

const OtpDigitInput = styled.input<{
  isShowError: boolean
  variant: string
  size: string
  color: string
  inputWidth: string
  inputHeight: string
}>`
  width: ${(props) => props.inputWidth};
  height: ${(props) => props.inputHeight};
  font-size: ${(props) => {
    switch (props.size) {
      case 'small':
        return '14px'
      case 'large':
        return '20px'
      default:
        return '16px'
    }
  }};
  font-weight: bold;
  text-align: center;
  border: ${(props) => {
    switch (props.variant) {
      case 'outlined':
        return `2px solid ${
          props.isShowError ? colors.danger : colors.grayInputBorder
        }`
      case 'filled':
        return `none`
      case 'standard':
        return `none`
      default:
        return `1px solid ${
          props.isShowError ? colors.danger : colors.grayInputBorder
        }`
    }
  }};
  border-radius: ${(props) => {
    switch (props.variant) {
      case 'filled':
      case 'standard':
        return '0'
      default:
        return '8px'
    }
  }};
  outline: none;
  transition: all 0.2s ease-in-out;
  background-color: ${(props) => {
    switch (props.variant) {
      case 'filled':
        return props.isShowError ? colors.dangerLight : colors.grayLighter
      case 'standard':
        return 'transparent'
      default:
        return colors.white
    }
  }};
  color: ${colors.grayDarker};

  &:focus {
    border-color: ${(props) => {
      switch (props.color) {
        case 'primary':
          return colors.primary
        case 'secondary':
          return colors.secondary
        case 'success':
          return colors.success
        case 'error':
          return colors.danger
        case 'warning':
          return colors.warning
        case 'info':
          return colors.info
        default:
          return colors.primary
      }
    }};
    box-shadow: 0 0 0 3px
      ${(props) => {
        const color = (() => {
          switch (props.color) {
            case 'primary':
              return colors.primary
            case 'secondary':
              return colors.secondary
            case 'success':
              return colors.success
            case 'error':
              return colors.danger
            case 'warning':
              return colors.warning
            case 'info':
              return colors.info
            default:
              return colors.primary
          }
        })()
        return `${color}20`
      }};
  }

  &:disabled {
    background-color: ${colors.grayLighter} !important;
    border-color: ${colors.grayInputBorder} !important;
    color: ${colors.grayMedium};
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${colors.grayMedium};
  }

  /* Hide number input arrows */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type='number'] {
    -moz-appearance: textfield;
  }
`

export const OtpInput = forwardRef<HTMLDivElement, OtpInputProps>(
  (
    {
      length = 6,
      value = '',
      onChange,
      onComplete,
      placeholder = '0',
      className,
      disabled = false,
      autoFocus = false,
      size = 'medium',
      variant = 'default',
      color = 'primary',
      isShowError = false,
      errorMessage,
      label,
      isShowLabel = true,
      isRequired = false,
      helperText,
      isShowHelperText = true,
      isShowErrorMessage = true,
      fullWidth = false,
      margin,
      padding,
      borderRadius,
      borderWidth,
      borderStyle,
      borderColor,
      backgroundColor,
      fontSize,
      fontWeight,
      fontFamily,
      textAlign,
      letterSpacing,
      lineHeight,
      boxShadow,
      gap = '10px',
      inputWidth = '50px',
      inputHeight = '50px',
      ...rest
    },
    ref,
  ) => {
    const inputRefs = useRef<(HTMLInputElement | null)[]>([])
    const otpDigits = value.split('').slice(0, length)
    const remainingDigits = length - otpDigits.length

    // Fill remaining slots with empty strings
    const displayDigits = [...otpDigits, ...Array(remainingDigits).fill('')]

    useEffect(() => {
      // Auto-focus first input if autoFocus is true
      if (autoFocus && inputRefs.current[0]) {
        inputRefs.current[0].focus()
      }
    }, [autoFocus])

    const handleOtpChange = (index: number, inputValue: string) => {
      // Only allow single digit input
      const newDigit = inputValue.slice(-1)
      if (!/^\d*$/.test(newDigit)) return // Only allow digits

      const newOtpDigits = [...otpDigits]
      newOtpDigits[index] = newDigit
      const newOtp = newOtpDigits.join('')

      onChange?.(newOtp)

      // Auto-focus next input
      if (newDigit && index < length - 1) {
        inputRefs.current[index + 1]?.focus()
      }

      // Call onComplete when all digits are filled
      if (newOtp.length === length) {
        onComplete?.(newOtp)
      }
    }

    const handleKeyDown = (
      index: number,
      e: React.KeyboardEvent<HTMLInputElement>,
    ) => {
      if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
        // Move to previous input on backspace if current is empty
        inputRefs.current[index - 1]?.focus()
      } else if (e.key === 'ArrowLeft' && index > 0) {
        // Move to previous input on left arrow
        inputRefs.current[index - 1]?.focus()
      } else if (e.key === 'ArrowRight' && index < length - 1) {
        // Move to next input on right arrow
        inputRefs.current[index + 1]?.focus()
      }
    }

    const handlePaste = (e: React.ClipboardEvent) => {
      e.preventDefault()
      const pastedData = e.clipboardData.getData('text/plain')
      const digits = pastedData.replace(/\D/g, '').slice(0, length)

      if (digits.length > 0) {
        onChange?.(digits)

        // Focus the next empty input or the last input
        const nextIndex = Math.min(digits.length, length - 1)
        inputRefs.current[nextIndex]?.focus()

        if (digits.length === length) {
          onComplete?.(digits)
        }
      }
    }

    return (
      <OtpContainer
        ref={ref}
        isShowError={isShowError}
        fullWidth={fullWidth}
        className={className}
        style={{
          margin,
          padding,
          borderRadius,
          borderWidth,
          borderStyle,
          borderColor,
          backgroundColor,
          fontSize,
          fontWeight,
          fontFamily,
          textAlign,
          letterSpacing,
          lineHeight,
          boxShadow,
        }}
      >
        {isShowLabel && label && (
          <div className="label">
            {label}
            {isRequired && <span className="required">*</span>}
          </div>
        )}

        <OtpInputGroup gap={gap}>
          {displayDigits.map((digit, index) => (
            <OtpDigitInput
              key={index}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              placeholder={placeholder}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              ref={(el) => {
                inputRefs.current[index] = el
              }}
              disabled={disabled}
              isShowError={isShowError}
              variant={variant}
              size={size}
              color={color}
              inputWidth={inputWidth}
              inputHeight={inputHeight}
              autoFocus={autoFocus && index === 0}
              {...rest}
            />
          ))}
        </OtpInputGroup>

        {isShowErrorMessage && isShowError && errorMessage && (
          <div className="error-message">{errorMessage}</div>
        )}

        {isShowHelperText && helperText && (
          <div className="helper-text">{helperText}</div>
        )}
      </OtpContainer>
    )
  },
)

OtpInput.displayName = 'OtpInput'

export default OtpInput
